import React, { useRef, useState, useCallback, useEffect, useContext } from 'react';
import sanitizeHtml from 'sanitize-html';
import { JWTAuthContext } from '../context/JWTAuthContext';
import './RichTextEditor.css';

const RichTextEditor = ({
  value = '',
  onChange,
  placeholder = '내용을 입력하세요...',
  disabled = false,
  height = '400px',
  localMode = false // 로컬 모드: true일 때 서버 업로드 대신 base64로 저장
}) => {
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const { jwtToken } = useContext(JWTAuthContext);

  // 텍스트 포맷팅 명령어 실행
  const execCommand = useCallback((command, value = null) => {
    if (disabled) return;
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  }, [disabled]);

  // 단순한 에디터 변경 핸들러
  const handleEditorChange = useCallback(() => {
    if (!editorRef.current || disabled) return;
    
    const content = editorRef.current.innerHTML;
    onChange?.(content);
  }, [disabled, onChange]);

  // 이미지 업로드 함수
  const uploadImage = useCallback(async (file) => {
    if (disabled) return;

    // 파일 크기 검증 (localMode: 5MB, 서버 모드: 10MB)
    const maxSize = localMode ? 5 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      alert(`이미지 파일 크기는 ${localMode ? '5MB' : '10MB'} 이하여야 합니다.`);
      return;
    }

    // 파일 타입 검증
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      alert('jpg, png, webp, gif 형식의 이미지만 업로드 가능합니다.');
      return;
    }

    // 로컬 모드: base64로 변환하여 직접 삽입
    if (localMode) {
      try {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = reader.result;
          const imgTag = `<img src="${base64}" alt="Pasted image" style="max-width: 100%; height: auto;" />`;
          document.execCommand('insertHTML', false, imgTag);
          editorRef.current?.focus();
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('❌ [RichTextEditor] 이미지 로드 실패:', error);
        alert('이미지 로드에 실패했습니다.');
      }
      return;
    }

    // 서버 모드: 서버에 업로드
    try {
      const formData = new FormData();
      formData.append('file', file);

      // travel-api 사용
      const apiUrl = `/travel-api/api/v1/images/upload`;

      console.log('📤 [RichTextEditor] 이미지 업로드 시작 (travel-api):', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        serverUrl: 'proxy',
        apiUrl: apiUrl
      });

      // Authorization 헤더 생성 (JWT 토큰 사용)
      const headers = {
        'Accept': 'application/json'
      };

      // JWT 토큰 가져오기 (Context 우선, 없으면 localStorage)
      const authToken = jwtToken || localStorage.getItem('jwtToken');

      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
        console.log('🔑 [RichTextEditor] Authorization 헤더 설정:', {
          tokenSource: jwtToken ? 'JWTContext' : 'localStorage',
          tokenLength: authToken.length,
          tokenStart: authToken.substring(0, 20) + '...'
        });
      } else {
        console.warn('⚠️ [RichTextEditor] 인증 토큰이 없습니다.');
        alert('로그인이 필요합니다. 이미지를 업로드하려면 먼저 로그인해주세요.');
        return;
      }

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: headers,
        body: formData
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [RichTextEditor] 업로드 실패 응답:', {
          status: response.status,
          statusText: response.statusText,
          errorText
        });
        throw new Error(`이미지 업로드에 실패했습니다: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ [RichTextEditor] 업로드 성공:', result);

      // 서버 응답에서 file_url 사용
      if (result.success && result.data && result.data.file_url) {
        let imageUrl = result.data.file_url;

        // 서버 URL을 프록시 경로로 변환
        // "http://localhost:8003/uploads/..." → "/travel-api/uploads/..."
        // "/uploads/..." → "/travel-api/uploads/..."
        if (imageUrl.includes('://')) {
          // 절대 URL인 경우
          const url = new URL(imageUrl);
          imageUrl = `/travel-api${url.pathname}`;
        } else if (imageUrl.startsWith('/uploads/')) {
          // 상대 경로인 경우
          imageUrl = `/travel-api${imageUrl}`;
        }

        console.log('🔄 [RichTextEditor] 이미지 URL (프록시용):', imageUrl);

        // 에디터에 이미지 삽입
        const imgTag = `<img src="${imageUrl}" alt="Uploaded image" style="max-width: 100%; height: auto;" />`;
        document.execCommand('insertHTML', false, imgTag);
        editorRef.current?.focus();

        console.log('🖼️ [RichTextEditor] 이미지 삽입 완료:', imageUrl);
      } else {
        throw new Error('서버에서 이미지 URL을 반환하지 않았습니다.');
      }
    } catch (error) {
      console.error('❌ [RichTextEditor] 이미지 업로드 실패:', error);
      alert(`이미지 업로드에 실패했습니다: ${error.message}`);
    }
  }, [disabled, localMode, jwtToken]);

  // 파일 선택 핸들러
  const handleFileSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadImage(file);
    }
    // input 초기화
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [uploadImage]);

  // 드래그 앤 드롭 핸들러
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    const imageFile = files.find(file => file.type.startsWith('image/'));
    
    if (imageFile) {
      uploadImage(imageFile);
    }
  }, [uploadImage]);

  // 붙여넣기 핸들러
  const handlePaste = useCallback((e) => {
    if (disabled) return;

    const items = Array.from(e.clipboardData?.items || []);
    const imageItem = items.find(item => item.type.startsWith('image/'));
    
    if (imageItem) {
      e.preventDefault();
      const file = imageItem.getAsFile();
      if (file) {
        uploadImage(file);
      }
    }
  }, [disabled, uploadImage]);

  // 키보드 단축키 처리만
  const handleKeyDown = useCallback((e) => {
    if (disabled) return;

    // 키보드 단축키 처리
    if (e.ctrlKey || e.metaKey) {
      switch (e.key) {
        case 'b':
          e.preventDefault();
          execCommand('bold');
          break;
        case 'i':
          e.preventDefault();
          execCommand('italic');
          break;
        case 'u':
          e.preventDefault();
          execCommand('underline');
          break;
        default:
          break;
      }
    }
  }, [disabled, execCommand]);

  // 에디터 클릭 핸들러 - 링크 편집 및 Ctrl+클릭으로 링크 열기
  const handleEditorClick = useCallback((e) => {
    if (disabled) return;

    const target = e.target.closest('a');
    if (!target) return;

    e.preventDefault();

    // Ctrl/Cmd + 클릭: 새 탭에서 링크 열기
    if (e.ctrlKey || e.metaKey) {
      window.open(target.href, '_blank', 'noopener,noreferrer');
      return;
    }

    // 일반 클릭: 링크 편집
    const currentUrl = target.href;
    const displayUrl = currentUrl.startsWith('http') ? currentUrl : `http://${currentUrl}`;
    const newUrl = prompt('링크 URL을 수정하세요:\n\n(Ctrl/Cmd+클릭으로 링크를 열 수 있습니다)', displayUrl);

    if (newUrl !== null) {
      if (newUrl.trim() === '') {
        // 빈 문자열이면 링크 제거
        target.outerHTML = target.textContent;
      } else {
        // URL 업데이트
        target.href = newUrl;
      }
      handleEditorChange();
    }
  }, [disabled, handleEditorChange]);

  // 링크 삽입
  const insertLink = useCallback(() => {
    if (disabled) return;

    const selection = window.getSelection();
    const selectedText = selection.toString();

    const url = prompt(
      '링크 URL을 입력하세요:\n\n' +
      '입력 후 Ctrl/Cmd+클릭으로 링크를 열 수 있습니다.\n' +
      '일반 클릭 시 링크를 편집할 수 있습니다.'
    );

    if (url) {
      if (selectedText) {
        // 텍스트가 선택되어 있으면 링크 생성
        execCommand('createLink', url);
      } else {
        // 선택된 텍스트가 없으면 URL을 텍스트로 하는 링크 생성
        const linkHtml = `<a href="${url}">${url}</a>`;
        document.execCommand('insertHTML', false, linkHtml);
      }
      editorRef.current?.focus();
    }
  }, [disabled, execCommand]);

  // 코드 블록 삽입
  const insertCodeBlock = useCallback(() => {
    if (disabled) return;
    
    const code = `<pre><code>${getSelection().toString() || '코드를 입력하세요'}</code></pre>`;
    document.execCommand('insertHTML', false, code);
    editorRef.current?.focus();
  }, [disabled]);

  // 체크리스트 삽입
  const insertChecklist = useCallback(() => {
    if (disabled) return;
    
    const checklistItem = `<div><input type="checkbox" disabled> 체크리스트 항목</div>`;
    document.execCommand('insertHTML', false, checklistItem);
    editorRef.current?.focus();
  }, [disabled]);

  // 외부에서 초기값 설정시에만 에디터 내용 업데이트
  useEffect(() => {
    if (editorRef.current && value && !editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  return (
    <div className={`rich-text-editor ${disabled ? 'disabled' : ''}`}>
      {/* 툴바 */}
      <div className="editor-toolbar">
        <div className="toolbar-group">
          <button 
            type="button"
            className="toolbar-btn"
            onClick={() => execCommand('bold')}
            disabled={disabled}
            title="굵게 (Ctrl+B)"
          >
            <strong>B</strong>
          </button>
          <button 
            type="button"
            className="toolbar-btn"
            onClick={() => execCommand('italic')}
            disabled={disabled}
            title="기울임 (Ctrl+I)"
          >
            <em>I</em>
          </button>
          <button 
            type="button"
            className="toolbar-btn"
            onClick={() => execCommand('underline')}
            disabled={disabled}
            title="밑줄 (Ctrl+U)"
          >
            <u>U</u>
          </button>
          <button 
            type="button"
            className="toolbar-btn"
            onClick={() => execCommand('strikeThrough')}
            disabled={disabled}
            title="취소선"
          >
            <s>S</s>
          </button>
        </div>

        <div className="toolbar-divider" />

        <div className="toolbar-group">
          <select 
            className="toolbar-select"
            onChange={(e) => execCommand('formatBlock', e.target.value)}
            disabled={disabled}
            defaultValue=""
          >
            <option value="">일반</option>
            <option value="<h1>">제목 1</option>
            <option value="<h2>">제목 2</option>
            <option value="<h3>">제목 3</option>
            <option value="<blockquote>">인용문</option>
          </select>
        </div>

        <div className="toolbar-divider" />

        <div className="toolbar-group">
          <button 
            type="button"
            className="toolbar-btn"
            onClick={() => execCommand('insertUnorderedList')}
            disabled={disabled}
            title="불릿 리스트"
          >
            • 목록
          </button>
          <button 
            type="button"
            className="toolbar-btn"
            onClick={() => execCommand('insertOrderedList')}
            disabled={disabled}
            title="번호 리스트"
          >
            1. 목록
          </button>
          <button 
            type="button"
            className="toolbar-btn"
            onClick={insertChecklist}
            disabled={disabled}
            title="체크리스트"
          >
            ☑ 체크
          </button>
        </div>

        <div className="toolbar-divider" />

        <div className="toolbar-group">
          <button 
            type="button"
            className="toolbar-btn"
            onClick={insertLink}
            disabled={disabled}
            title="링크 삽입"
          >
            🔗 링크
          </button>
          <button 
            type="button"
            className="toolbar-btn"
            onClick={() => execCommand('insertHTML', '<code>코드</code>')}
            disabled={disabled}
            title="인라인 코드"
          >
            &lt;/&gt;
          </button>
          <button 
            type="button"
            className="toolbar-btn"
            onClick={insertCodeBlock}
            disabled={disabled}
            title="코드 블록"
          >
            {} 블록
          </button>
        </div>

        <div className="toolbar-divider" />

        <div className="toolbar-group">
          <button 
            type="button"
            className="toolbar-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled}
            title="이미지 업로드"
          >
            🖼️ 이미지
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </div>
      </div>

      {/* 에디터 */}
      <div
        ref={editorRef}
        className={`editor-content ${isDragging ? 'dragging' : ''}`}
        contentEditable={!disabled}
        suppressContentEditableWarning={true}
        style={{ height }}
        onInput={handleEditorChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onClick={handleEditorClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        data-placeholder={placeholder}
      />

      {isDragging && (
        <div className="drag-overlay">
          <div className="drag-message">
            <div className="drag-icon">📁</div>
            <p>이미지를 여기에 드롭하세요</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default RichTextEditor;